import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, Observable } from 'rxjs';
import { Load } from 'src/app/working-table/visualisation/load';
import { Project } from '../objects/project';
import { Axle, CargoSpace, Group, LoadsCube, PalletLayerGroup, WorkSpace } from '../types';
import { SharedProjectService } from './shared-project.service';
import { ActivatedRoute } from '@angular/router';
import { MainView } from '../../working-table/visualisation/main-view';
import { take } from 'rxjs/operators';
import { getFromLocalStorage } from '../../main-panel/utils/localStorageHelpers';
import { convertProjectDimensions } from '../../shared/helpers/unitRecalculationHelpers';
import { getPalletLayerGroupsFromCargoSpace } from '../../shared/helpers/pallet-loading-space.helper';

@Injectable({
  providedIn: 'root',
})
export class ProjectService {
  public $workingTable: BehaviorSubject<MainView | null>;
  public project: Observable<Project>;
  public currentWorkSpaceIndex: Observable<number>;
  public currentStopIndex: Observable<number>;
  public closedLoadingSpaces: Observable<number[]>;
  public currentLoadGroupId: Observable<{ id: number; object?: Load }>;
  private projectSubject: BehaviorSubject<Project>;
  private currentWorkSpaceIndexSubject: BehaviorSubject<number>;
  private currentStopIndexSubject: BehaviorSubject<number>;
  private closeLoadingSpacesSubject: BehaviorSubject<number[]>;
  private currentLoadGroupIdSubject: BehaviorSubject<{ id: number; object?: Load }>;
  private isViewingShared: boolean;
  public isOpened = false;
  private closedLoadingSpacesArray: number[];
  public currentNumberOfLoads: number;
  public maxNumberOfLoads = 15000;
  public isEmapa = false;

  constructor(
    private cookieService: CookieService,
    private sharedProjectService: SharedProjectService,
    private translate: TranslateService,
    private route: ActivatedRoute,
  ) {
    this.$workingTable = new BehaviorSubject<MainView | null>(null);
    this.projectSubject = new BehaviorSubject(new Project());
    this.project = this.projectSubject.asObservable();
    this.currentWorkSpaceIndexSubject = new BehaviorSubject(0);
    this.currentWorkSpaceIndex = this.currentWorkSpaceIndexSubject.asObservable();
    this.currentStopIndexSubject = new BehaviorSubject(undefined);
    this.currentStopIndex = this.currentStopIndexSubject.asObservable();
    this.currentLoadGroupIdSubject = new BehaviorSubject({ id: undefined });
    this.currentLoadGroupId = this.currentLoadGroupIdSubject.asObservable();

    this.sharedProjectService.isViewingShared?.subscribe(
      (isViewingShared: boolean) => (this.isViewingShared = isViewingShared),
    );
    if (this.cookieService.get('closedLoadingSpaces') && !this.isViewingShared) {
      const cookieClosedLoadingSpaces = this.cookieService.get('closedLoadingSpaces');
      const arrayClosedLoadingSpaces = JSON.parse(cookieClosedLoadingSpaces) as number[];
      this.closeLoadingSpacesSubject = new BehaviorSubject(arrayClosedLoadingSpaces);
    } else {
      this.closeLoadingSpacesSubject = new BehaviorSubject([] as number[]);
    }
    this.closedLoadingSpaces = this.closeLoadingSpacesSubject.asObservable();

    this.closedLoadingSpaces.subscribe((closedLoadingSpaces) => {
      this.closedLoadingSpacesArray = closedLoadingSpaces;
      const jsonClosedLoadingSpaces = JSON.stringify(closedLoadingSpaces);
      this.cookieService.set(
        'closedLoadingSpaces',
        jsonClosedLoadingSpaces,
        undefined,
        '/',
        null,
        false,
        'Lax',
      );
    });
  }

  private setCookie(name: string, value: string): void {
    this.cookieService.set(name, value, undefined, '/');
  }

  private setPreferences(): void {
    this.route.queryParams.pipe(take(2)).subscribe((queryParams) => {
      if (Object.hasOwnProperty.call(queryParams, 'pref')) {
        const preferences: string[] = queryParams.pref.split('-');
        if (preferences.includes('opened')) {
          this.isOpened = true;
        }
        if (preferences.includes('opt')) {
          this.setCookie('optimization', 'true');
        } else {
          this.setCookie('optimization', 'false');
        }
        if (preferences.includes('names')) {
          this.setCookie('loadName', 'true');
        } else {
          this.setCookie('loadName', 'false');
        }
        if (preferences.includes('measure')) {
          this.setCookie('measure', 'true');
        } else {
          this.setCookie('measure', 'false');
        }
      }
    });
  }

  public isProjectEmpty(): boolean {
    return this.getState().workSpaces.length === 0;
  }

  public getState(): Project {
    return this.projectSubject.getValue();
  }

  private prepareAxles(project: Project): void {
    if (!project || !project.workSpaces) return;
    project.workSpaces.forEach((workSpace: WorkSpace) => {
      if (!workSpace.axis || !workSpace.cargoSpaces || workSpace.cargoSpaces.length === 0) return;

      const allCargoSpaceAxles: Axle[] = [];
      workSpace.cargoSpaces.forEach((cargoSpace: CargoSpace) => {
        if (cargoSpace.axis) {
          allCargoSpaceAxles.push(...cargoSpace.axis);
        }
      });

      allCargoSpaceAxles.forEach((axle: Axle, index: number) => {
        if (index < workSpace.axis.length) {
          workSpace.axis[index].addedLoad = axle.addedLoad;
        }
      });
      if (workSpace.stops && workSpace.stops.length > 0) {
        workSpace.stops.forEach((stop) => {
          if (!stop.axis || !stop.cargoSpaces || stop.cargoSpaces.length === 0) return;

          const allStopCargoSpaceAxles: Axle[] = [];
          stop.cargoSpaces.forEach((cargoSpace: CargoSpace) => {
            if (cargoSpace.axis) {
              allStopCargoSpaceAxles.push(...cargoSpace.axis);
            }
          });

          allStopCargoSpaceAxles.forEach((axle: Axle, index: number) => {
            if (index < stop.axis.length) {
              stop.axis[index].addedLoad = axle.addedLoad;
            }
          });
        });
      }
    });
  }

  public getCurrentWorkSpace() {
    return this.currentWorkSpaceIndexSubject.getValue();
  }

  public setCurrentWorkSpace(currentWorkSpace: number) {
    return this.currentWorkSpaceIndexSubject.next(currentWorkSpace);
  }

  public getCurrentStopIndex() {
    return this.currentStopIndexSubject.getValue();
  }

  public setCurrentStopIndex(stopIndex: number) {
    return this.currentStopIndexSubject.next(stopIndex);
  }

  public setCurrentLoadGroupId(currentLoadGroupId: number, object?: Load) {
    return this.currentLoadGroupIdSubject.next({ id: currentLoadGroupId, object });
  }

  public setCurrentNumberOfLoads(project: Project): void {
    let numberOfLoads = 0;
    const isMultiSpace = project.workSpaces[this.getCurrentWorkSpace()]?.stops
      ? project.workSpaces[this.getCurrentWorkSpace()].stops?.length > 0
      : false;
    if (isMultiSpace) {
      const allGroups = [];
      project.workSpaces[this.getCurrentWorkSpace()].stops.forEach((stop) => {
        stop.cargoSpaces.forEach((cargoSpace: CargoSpace) => {
          cargoSpace.groups.forEach((group: Group) => allGroups.push(group));
        });
      });
      const uniqueGroupsLoadNumber = allGroups
        .filter(
          (group, index: number, self): boolean =>
            self.findIndex((g): boolean => g.id === group.id) === index,
        )
        .map((group: Group) => group.quantity);
      const uniqueGroupsLoadsNumber: number = uniqueGroupsLoadNumber.reduce(
        (a: number, b: number) => a + b,
        0,
      );
      numberOfLoads = uniqueGroupsLoadsNumber;
    } else {
      project.workSpaces.forEach((workSpace: WorkSpace) => {
        workSpace.cargoSpaces.forEach((cargoSpace: CargoSpace) => {
          cargoSpace.groups.forEach((group: Group) => (numberOfLoads += group.quantity));
        });
      });
      project.workSpaces.forEach((workSpace: WorkSpace) => {
        workSpace.outsideSpace.groups.forEach((group: Group) => {
          numberOfLoads += group.quantity;
        });
      });
    }
    this.currentNumberOfLoads = numberOfLoads;
  }

  public resetRecommendations(): void {
    const modifiedProject = this.getState();
    modifiedProject.recommendations = undefined;
    modifiedProject.options.calculateRecommendations = false;
    this.projectSubject.next(modifiedProject);
  }

  public setState(project: Project, currentWorkSpace?: number, stopIndex?: number): void {
    this.setCurrentNumberOfLoads(project);
    this.sharedProjectService.isViewingShared.subscribe((isViewing: boolean) => {
      if (isViewing) {
        this.setPreferences();
      }
      if (currentWorkSpace >= 0) {
        this.currentWorkSpaceIndexSubject.next(currentWorkSpace);
      }

      if (stopIndex >= 0) {
        this.currentStopIndexSubject.next(stopIndex);
      }

      if (project?.workSpaces.length > 0) {
        project.workSpaces.forEach((space: WorkSpace) => {
          this.setGroupOptions(space);
          space.stops?.forEach((stop) => this.setGroupOptions(stop));
        });

        this.cookieService.get('backlight').includes('true')
          ? (project.options.backlightOn = true)
          : (project.options.backlightOn = false);
        this.cookieService.get('measure').includes('true')
          ? (project.options.measure = true)
          : (project.options.measure = false);
      }

      if (
        !(this.getCurrentStopIndex() >= 0) &&
        project.workSpaces[this.getCurrentWorkSpace()]?.stops?.length > 0
      ) {
        this.setCurrentStopIndex(0);
      } else if (
        this.getCurrentStopIndex() >= 0 &&
        !(project.workSpaces[this.getCurrentWorkSpace()]?.stops?.length > 0)
      ) {
        this.setCurrentStopIndex(undefined);
      }
      if (
        this.isMmUnitInLocalStorage() &&
        project.options.unit !== 'lb' &&
        !project.isConverted &&
        !this.isViewingShared
      ) {
        project.options.unit = 'mm';
        project.isConverted = true;
        project = {...convertProjectDimensions(project, true)};
      }
      this.prepareAxles(project);
      this.projectSubject.next(project);
    });
  }

  private isMmUnitInLocalStorage(): boolean {
    return getFromLocalStorage('unit').data === 'mm';
  }

  private setGroupOptions(space: WorkSpace) {
    space.cargoSpaces?.forEach((cargo: CargoSpace) => {
      cargo.groups?.forEach((group: Group) => {
        this.applyGroupDisplayOptions(group);
      });
      getPalletLayerGroupsFromCargoSpace(cargo).forEach((palletGroup) => {
        this.applyPalletLayerGroupDisplayOptions(palletGroup);
      });
    });

    space.outsideSpace?.groups.forEach((group: Group) => {
      this.applyGroupDisplayOptions(group);
    });

    space.unloadingSpace?.groups.forEach((group: Group) => {
      this.applyGroupDisplayOptions(group);
    });
  }

  private applyGroupDisplayOptions(group: Group): void {
    group.nameTranslated = this.translate.instant(group.name) as string;
    group.showLoadName = this.cookieService.get('loadName').includes('true');
    group.isOptimization = true;
  }

  private applyPalletLayerGroupDisplayOptions(palletGroup: PalletLayerGroup): void {
    if (palletGroup.name) {
      palletGroup.nameTranslated = this.translate.instant(palletGroup.name) as string;
    }
    palletGroup.showLoadName = this.cookieService.get('loadName').includes('true');
  }

  public setClosedLoadingSpaces(closedLoadingSpaces: number[]) {
    return this.closeLoadingSpacesSubject.next(closedLoadingSpaces);
  }

  public openClosedLoadingSpace(index: number) {
    if (this.isClosedLoadingSpace(index)) {
      const closed = this.closedLoadingSpacesArray.filter((closeIndex) => closeIndex !== index);
      this.setClosedLoadingSpaces(closed);
    }
  }

  public isClosedLoadingSpace(index: number) {
    const isClose = this.closedLoadingSpacesArray.findIndex((closeIndex) => closeIndex === index);
    if (isClose >= 0) {
      return true;
    } else {
      return false;
    }
  }

  public getProjectQuantity(): number {
    return this.currentNumberOfLoads;
  }

  public getProjectGroupOrder(groups: Group[]) {
    groups = this.divideGroups(groups);
    groups = this.divideGroups(groups);
    groups = this.sortingGroup(groups);
    groups = this.orderGroups(groups);
    groups = this.addLoadsCubesToGroup(groups);
    return groups;
  }

  private divideGroups(groups: Group[]) {
    const newGroups: Group[] = [];
    groups.forEach((group: Group) => {
      group.loadsCubes.forEach((loadCube: LoadsCube) => {
        const newGroup = _.cloneDeep(group);
        const loadsCube: LoadsCube[] = [];
        for (let i = 0; i < loadCube.loadsPerAxis.z; i++) {
          for (let j = 0; j < loadCube.loadsPerAxis.x; j++) {
            for (let k = 0; k < loadCube.loadsPerAxis.y; k++) {
              const load: LoadsCube = _.cloneDeep(loadCube);
              load.loadsPerAxis.x = 1;
              load.loadsPerAxis.y = 1;
              load.loadsPerAxis.z = 1;

              if (load.dimensions) {
                load.position.x = load.position.x + load.dimensions.width * j;
                load.position.y = load.position.y + load.dimensions.height * k;
                load.position.z = load.position.z + load.dimensions.length * i;
              } else {
                load.position.x = load.position.x + group.dimensions.width * j;
                load.position.y = load.position.y + group.dimensions.height * k;
                load.position.z = load.position.z + group.dimensions.length * i;
              }
              loadsCube.push(load);
            }
          }
        }
        newGroup.loadsCubes = loadsCube;
        newGroup.quantity = newGroup.loadsCubes.length;
        newGroups.push(newGroup);
      });
    });
    return newGroups;
  }

  private sortingGroup(groups: Group[]) {
    const newGroups = groups.sort((a: Group, b: Group) => {
      if (a.loadsCubes[0].position.z - b.loadsCubes[0].position.z !== 0) {
        return a.loadsCubes[0].position.z - b.loadsCubes[0].position.z;
      }

      if (a.loadsCubes[0].position.x - b.loadsCubes[0].position.x !== 0) {
        return a.loadsCubes[0].position.x - b.loadsCubes[0].position.x;
      }

      return a.loadsCubes[0].position.y - b.loadsCubes[0].position.y;
    });

    return newGroups;
  }

  private addLoadsCubesToGroup(groups: Group[]) {
    const newGroups: Group[] = [];
    let lastGroup: Group;

    groups.forEach((group) => {
      if (!lastGroup) {
        lastGroup = group;
        return;
      }
      if (lastGroup.id === group.id) {
        lastGroup.loadsCubes.push(...group.loadsCubes);
      } else {
        lastGroup.quantity = lastGroup.loadsCubes.length;
        newGroups.push(lastGroup);
        lastGroup = group;
      }
    });
    if (lastGroup?.loadsCubes) {
      lastGroup.quantity = lastGroup.loadsCubes.length;
      newGroups.push(lastGroup);
    }
    return newGroups;
  }

  private orderGroups(groups: Group[]) {
    return groups.sort((a: Group, b: Group) => {
      const dimA = a.loadsCubes[a.loadsCubes.length - 1].dimensions
        ? a.loadsCubes[a.loadsCubes.length - 1].dimensions
        : a.dimensions;
      const dimB = b.loadsCubes[0].dimensions ? b.loadsCubes[0].dimensions : b.dimensions;
      const POSITION_X_A = a.loadsCubes[0].position.x;
      const POSITION_Z_A = a.loadsCubes[0].position.z;
      const POSITION_X_B = b.loadsCubes[0].position.x;
      const POSITION_Z_B = b.loadsCubes[0].position.z;

      const POSITION_LAST_X_A = a.loadsCubes[a.loadsCubes.length - 1].position.x;
      const POSITION_LAST_Z_A = a.loadsCubes[a.loadsCubes.length - 1].position.z;
      const POSITION_LAST_X_B = b.loadsCubes[b.loadsCubes.length - 1].position.x;
      const POSITION_LAST_Z_B = b.loadsCubes[b.loadsCubes.length - 1].position.z;

      if (
        (POSITION_X_A <= POSITION_X_B &&
          POSITION_LAST_X_A + dimA.width > POSITION_X_B &&
          POSITION_Z_A <= POSITION_Z_B &&
          POSITION_LAST_Z_A + dimA.length > POSITION_Z_B) ||
        (POSITION_LAST_X_A + dimA.width > POSITION_X_B &&
          POSITION_LAST_X_A + dimA.width <= POSITION_LAST_X_B + dimB.width &&
          POSITION_Z_A < POSITION_LAST_Z_B + dimB.length &&
          POSITION_LAST_Z_A + dimA.length >= POSITION_LAST_Z_B + dimB.length) ||
        (POSITION_X_A < POSITION_LAST_X_B + dimB.width &&
          POSITION_X_A >= POSITION_X_B &&
          POSITION_Z_A >= POSITION_Z_B &&
          POSITION_Z_A < POSITION_LAST_Z_B + dimB.length) ||
        (POSITION_X_A < POSITION_LAST_X_B + dimB.width &&
          POSITION_LAST_X_A + dimA.width >= POSITION_LAST_X_B + dimB.width &&
          POSITION_LAST_Z_A + dimA.length > POSITION_Z_B &&
          POSITION_LAST_Z_A + dimA.length <= POSITION_LAST_Z_B + dimB.length)
      ) {
        return a.loadsCubes[0].position.y - b.loadsCubes[0].position.y;
      }
    });
  }
}
